import React, { useContext, useEffect, useState, useRef } from "react";
import { Modal, Box, Typography, CircularProgress } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import NeoButton from "./NeoButton";
import gameMenuBg from "../@shared/assets/game_menu.png";
import { Close, ChevronLeft, ChevronRight } from "@mui/icons-material";
import { MainContext } from "../App";
import {
  HOW_TO_PLAY,
  LEVELING_AND_REWARDS,
  STRATEGY_GUIDE,
  DECK_BUILDING,
  QUESTING,
} from "../@shared/utils/howToPlayUtils";
import bronzeBadge from "../@shared/assets/badges/bronze_badge.png";
import silverBadge from "../@shared/assets/badges/silver_badge.png";
import goldBadge from "../@shared/assets/badges/gold_badge.png";
import eliteBadge from "../@shared/assets/badges/elite_badge.png";
import legendBadge from "../@shared/assets/badges/legend_badge.png";
import mythicBadge from "../@shared/assets/badges/mythic_badge.png";
import masterBadge from "../@shared/assets/badges/master_badge.png";
import infinityMasterBadge from "../@shared/assets/badges/infinty_master_badge.png";

const dummyPoints = {
  damageDealtPoints: 100,
  eliminationPoints: 50,
  placementPoints: 300,
  playedCardPoints: 100,
  discardCardPoints: 50,
  tradeCardPoints: 75,
};

// Styles
const backdropStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  width: "100vw",
  height: "100vh",
  backgroundColor: "rgba(0,0,0,0.6)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 1400,
};

const modalWrapperStyle = {
  width: "90vw",
  maxWidth: "600px",
  aspectRatio: 4 / 5,
  borderRadius: "16px",
  position: "relative",
};

const modalContentStyle = {
  width: "100%",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  padding: "5% 8% 12%",
  backgroundImage: `url(${gameMenuBg})`,
  backgroundSize: "cover",
  position: "relative",
};

export default function HowToPlayModal({ visible, onClose }) {
  const { Theme } = useContext(MainContext);
  const [tab, setTab] = useState(0);
  const scrollRef = useRef();

  const tabs = [
    HOW_TO_PLAY,
    LEVELING_AND_REWARDS,
    DECK_BUILDING,
    QUESTING,
    STRATEGY_GUIDE,
  ];

  const badgeLevels = [
    { level: 1, image: bronzeBadge },
    { level: 25, image: silverBadge },
    { level: 50, image: goldBadge },
    { level: 65, image: eliteBadge },
    { level: 75, image: legendBadge },
    { level: 85, image: mythicBadge },
    { level: "M1", image: masterBadge },
    { level: "M2+", image: infinityMasterBadge },
  ];
  const handleScroll = (direction) => {
    if (!scrollRef.current) return;
    const scrollAmount = 200;
    scrollRef.current.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  const backdropStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    backgroundColor: "rgba(0,0,0,0.6)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1400,
  };

  const modalWrapperStyle = {
    width: "90vw",
    maxWidth: "600px",
    aspectRatio: 4 / 5,
    borderRadius: "16px",
    position: "relative",
  };

  const modalContentStyle = {
    width: "80%",
    height: "80%",
    display: "flex",
    flexDirection: "column",
    padding: "5% 8% 12%",
    backgroundImage: `url(${gameMenuBg})`,
    backgroundSize: "cover",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
    position: "relative",
  };

  return (
    <>
      <Modal open={visible} onClose={onClose} closeAfterTransition>
        <AnimatePresence>
          {visible && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={backdropStyle}
            >
              <motion.div
                initial={{ y: -50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -50, opacity: 0 }}
                transition={{ duration: 0.3 }}
                style={modalWrapperStyle}
              >
                <Box sx={modalContentStyle}>
                  {/* Title */}
                  <Typography
                    color="#fff"
                    fontWeight="bold"
                    variant="h5"
                    align="center"
                    sx={{ mt: 1, mb: 1 }}
                  >
                    How To Play
                  </Typography>
                  {/* Content */}

                  {/* Scrollable Tabs with Arrow Buttons */}
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 2,
                      gap: 1,
                    }}
                  >
                    {/* Left Arrow */}
                    <NeoButton
                      isIcon
                      icon={<ChevronLeft />}
                      color={Theme.palette.secondary.main}
                      onClick={() => handleScroll("left")}
                    />

                    {/* Scroll Container */}
                    <Box
                      ref={scrollRef}
                      sx={{
                        overflowX: "auto",
                        whiteSpace: "nowrap",
                        flex: 1,
                        maxWidth: "calc(100% - 110px)",
                        display: "flex",
                        gap: 1,
                        paddingBottom: 0.5,
                        // Hide scrollbars cross-browser
                        scrollbarWidth: "none", // Firefox
                        msOverflowStyle: "none", // IE/Edge
                        "&::-webkit-scrollbar": {
                          display: "none", // Chrome/Safari
                        },
                      }}
                    >
                      {tabs.map((t, index) => (
                        <NeoButton
                          key={index}
                          text={t.title}
                          onClick={() => setTab(index)}
                          color={
                            tab === index ? Theme.palette.primary.main : "#444"
                          }
                        />
                      ))}
                    </Box>

                    {/* Right Arrow */}
                    <NeoButton
                      isIcon
                      icon={<ChevronRight />}
                      color={Theme.palette.secondary.main}
                      onClick={() => handleScroll("right")}
                    />
                  </Box>

                  {/* Scrollable Content */}
                  <Box
                    sx={{
                      flex: 1,
                      overflowY: "auto",
                      // Hide scrollbars cross-browser
                      scrollbarWidth: "none", // Firefox
                      msOverflowStyle: "none", // IE/Edge
                      "&::-webkit-scrollbar": {
                        display: "none", // Chrome/Safari
                      },
                      backgroundColor: "#00000055",
                      padding: 2,
                      borderRadius: 2,
                    }}
                  >
                    {tabs[tab].sections.map((section, idx) => (
                      <Box key={idx} sx={{ mb: 2 }}>
                        <Typography
                          variant="h6"
                          fontWeight="bold"
                          style={{ color: Theme.palette.text.main }}
                        >
                          {section.heading}
                        </Typography>
                        {tab === 1 && section.heading === "Level Tiers" && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "left",
                              mt: 1,
                              mb: 1,
                            }}
                          >
                            {badgeLevels.map((badge, index) => (
                              <Box
                                key={index}
                                sx={{
                                  mr: index > 2 ? 1.5 : 0.5,
                                  height: 40,
                                  width: 40,
                                  position: "relative",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <img
                                  src={badge.image}
                                  alt={badge.level}
                                  style={{
                                    width: 40,
                                    height: 40,
                                    top: 0,
                                    left: 0,
                                    position: "absolute",
                                    zIndex: 0,
                                  }}
                                />
                                <Typography
                                  variant="body2"
                                  color="#ccc"
                                  whiteSpace="pre-line"
                                  style={{
                                    zIndex: 1,
                                    fontWeight: "bold",
                                    fontSize: 12,
                                    textShadow: ` -1px -1px 0 #000, 1px -1px 0 #000, -1px  1px 0 #000, 1px  1px 0 #000`,
                                  }}
                                >
                                  {badge.level}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        )}
                        <Typography
                          variant="body2"
                          color="#ccc"
                          whiteSpace="pre-line"
                        >
                          {section.content}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  {/* Close Button */}
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: "-2%",
                      left: "50%",
                      transform: "translateX(-50%)",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <NeoButton
                      isIcon={true}
                      icon={<Close />}
                      color={Theme.palette.secondary.main}
                      onClick={onClose}
                    />
                  </Box>
                </Box>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </Modal>
    </>
  );
}
